

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewOrderCommModal from 'components/NewOrderCommModal';

const OrderComms = () => {
    const [comms, setComms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('order_comms');

    useEffect(() => {
        fetchComms();
    }, []);

    const fetchComms = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/order-comms/');
            setComms(response.data);
        } catch (error) {
            console.error('Error fetching order comms:', error);
            setSnackbar({ open: true, message: 'Error loading order comms', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveComm = async (payload) => {
        try {
            await axiosInstance.post('/order-comms/', payload);
            setSnackbar({ open: true, message: 'Order Comm created successfully', severity: 'success' });
            fetchComms();
        } catch (error) {
            console.error('Error saving order comm:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/order-comm/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Comm Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Travel Order",
            accessor: "travel_order_id",
            width: "15%",
            Cell: ({ row }) => row.original.travel_order?.name || row.original.travel_order_id
        },
        {
            Header: "Passenger",
            accessor: "order_pax_id",
            width: "15%",
            Cell: ({ row }) => row.original.order_pax?.passenger_name || row.original.order_pax_id
        },
        {
            Header: "Email",
            accessor: "email",
            width: "20%"
        },
        {
            Header: "Status",
            accessor: "status",
            width: "10%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Sent'}
                    size="small"
                    color={value === 'Failed' ? 'error' : 'success'}
                />
            )
        },
        {
            Header: "Template ID",
            accessor: "template_id",
            width: "15%"
        },
        {
            Header: "Last Sent By",
            accessor: "last_sent_by_id",
            width: "10%",
            Cell: ({ row }) => row.original.last_sent_by?.fullname || row.original.last_sent_by_id || '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: comms,
    }), [comms]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Order Communications</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Communication
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : comms.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Order Communication" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewOrderCommModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveComm}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default OrderComms;

